import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
	updateDisplayName,
	updateEmailAddress,
	verifyEmail,
	sendEmailVerificationCode,
	getUserInfo,
	updateUserInfo,
} from "../actions/userAuthActions";
import { ButtonWithIcon } from "./ButtonWithIcon";
import { validateDisplayName } from "../utilities/validationUtils";
import { LabeledTextInput } from "./TextInput";
import { LexWorkplaceCheckIcon } from "./LexWorkplaceIcons";
import { InfoFieldSection } from "./EditableInfoField";
import "./InfoPageLayout.css";
import { IconAlertTriangleFilled } from "@tabler/icons-react";

const errorMessages = {
	newPasswordsMatch: "New passwords do not match",
	newPasswordLength: "New password must be at least 8 characters",
	oldPasswordEntered: "Please enter existing password",
	specialCharactersCompliant:
		"New password must include a lowercase letter, uppercase letter, number AND special character",
	codeIsEntered:
		"Please enter the verification code you received in an email",
};

class MyProfile extends Component {
	constructor(props) {
		super(props);
		this.state = {
			name: "",
			email: "",
			code: "",
			canSubmitEmail: false,
			canSubmitDisplayName: false,
			nameIsDifferent: false,
			nameConforms: false,
			emailIsDifferent: false,
			emailConforms: false,
			errorMessage: "",
		};
		this.handleChange = this.handleChange.bind(this);
		this.submitDisplayName = this.submitDisplayName.bind(this);
		this.submitEmail = this.submitEmail.bind(this);
		this.submitCode = this.submitCode.bind(this);
		this.resendCode = this.resendCode.bind(this);
		this.validate = this.validate.bind(this);
		this.generateErrorMessage = this.generateErrorMessage.bind(this);
	}
	componentDidMount() {
		this.validate();
		// the API layer surfaces its own error toast; swallow here so a failed
		// load isn't an unhandled rejection
		this.props.getUserInfo().catch(() => {});
	}
	handleChange(key, value) {
		this.setState({ [key]: value }, this.validate);
	}
	generateErrorMessage() {
		return Object.keys(errorMessages)
			.filter((errorKey) => {
				return !this.state[errorKey];
			})
			.map((errorKey) => {
				return <div key={errorKey}>{errorMessages[errorKey]}</div>;
			});
	}
	validate() {
		const trimmedName = this.state.name.trim();
		const nameIsDifferent = trimmedName !== this.props.userDisplayName;
		const nameConforms =
			validateDisplayName(trimmedName) && trimmedName.length < 65;
		const emailIsDifferent = this.state.email !== this.props.email;
		const emailConforms = /.+@.+\..+/.test(this.state.email);
		const canSubmitDisplayName = nameIsDifferent && nameConforms;
		const canSubmitEmail = emailIsDifferent && emailConforms;
		this.setState({
			nameIsDifferent,
			nameConforms,
			emailIsDifferent,
			emailConforms,
			canSubmitDisplayName,
			canSubmitEmail,
		});
	}
	submitDisplayName() {
		if (this.state.canSubmitDisplayName) {
			this.props.updateDisplayName(this.state.name.trim());
		}
	}
	submitEmail() {
		const { email } = this.state;
		if (this.state.canSubmitEmail) {
			this.props.updateEmailAddress(email);
		}
	}
	submitCode() {
		this.props.verifyEmail(this.state.code);
	}
	resendCode() {
		const stateEmail = this.state.email;
		const propsEmail = this.props.email;
		console.log({ stateEmail, propsEmail });
		this.props.sendEmailVerificationCode(propsEmail);
	}
	render() {
		return (
			<div className="InfoPageLayout">
				<div className="InfoPageColumn">
					<LabeledTextInput
						label="Display name"
						name="name"
						defaultValue={this.props.userDisplayName}
						onChange={this.handleChange}
					/>
					<ButtonWithIcon
						disabled={!this.state.canSubmitDisplayName}
						handler={this.submitDisplayName}
						buttonText="Update Display Name"
						iconComponent={<LexWorkplaceCheckIcon />}
					/>
					<div style={{ marginTop: 24 }}>
						<div>
							<LabeledTextInput
								label="Email Address"
								name="email"
								defaultValue={this.props.email}
								onChange={this.handleChange}
							/>

							<ButtonWithIcon
								disabled={!this.state.canSubmitEmail}
								handler={this.submitEmail}
								buttonText="Update Email Address"
								iconComponent={<LexWorkplaceCheckIcon />}
							/>
						</div>
						{!this.props.emailVerified ? (
							<div style={{ marginTop: 12 }}>
								<div
									style={{
										marginBottom: 12,
										display: "flex",
										gap: 16,
									}}
								>
									<div
										style={{
											display: "flex",
											// vertically centers the icon
											flexDirection: "column",
											justifyContent: "space-around",
										}}
									>
										<IconAlertTriangleFilled
											size={32}
											color={"var(--lw-darker-gold)"}
										/>
									</div>
									<div>
										Check your email for a verification
										code. Until the new email address is
										verified, you will not be able to log in
										using the new email address
									</div>
								</div>
								<LabeledTextInput
									label="Verification Code"
									name="code"
									placeholder="Code"
									autocomplete="off"
									onChange={this.handleChange}
								/>

								<div style={{ display: "flex", gap: 12 }}>
									<ButtonWithIcon
										buttonText="Re-send Code"
										iconComponent={
											<LexWorkplaceCheckIcon />
										}
										handler={this.resendCode}
									/>
									<ButtonWithIcon
										disabled={this.state.code === ""}
										buttonText="Verify Email"
										iconComponent={
											<LexWorkplaceCheckIcon />
										}
										handler={this.submitCode}
									/>
								</div>
							</div>
						) : null}
					</div>
				</div>
				<div className="InfoPageSideColumn">
					<InfoFieldSection
						heading="Custom AI Instructions (Just My Login)"
						description="Enter any custom instructions or context that you want to be applied to every AI conversation (note these instructions apply to you only). Doing so will fine-tune your AI experience to your personal preferences."
						as="textarea"
						name="customAiInstructions"
						height={300}
						loading={this.props.userInfoLoading}
						initialValue={this.props.userInfo.customAiInstructions}
						onSave={(value) =>
							this.props.updateUserInfo({
								customAiInstructions: value,
							})
						}
					/>
				</div>
			</div>
		);
	}
}
MyProfile.propTypes = {
	userDisplayName: PropTypes.string,
	email: PropTypes.string.isRequired,
	emailVerified: PropTypes.bool,
	errorMessage: PropTypes.string.isRequired,
	userInfo: PropTypes.object,
	userInfoLoading: PropTypes.bool,
};
const mapStateToProps = (state) => ({
	userDisplayName: state.user.userDisplayName,
	email: state.user.userEmail,
	emailVerified: state.user.emailVerified,
	errorMessage: state.user.errorMessage,
	userInfo: state.user.userInfo,
	userInfoLoading: state.user.userInfoLoading,
});
export default connect(mapStateToProps, {
	verifyEmail,
	sendEmailVerificationCode,
	updateDisplayName,
	updateEmailAddress,
	getUserInfo,
	updateUserInfo,
})(MyProfile);
